import type { TextInputProps } from 'src/UIKit/components/TextInput';
import { SxProps, Theme } from "@mui/material";
import React, { ReactElement, ReactNode } from "react";
import { Control, Controller, FieldValues, Path, RegisterOptions, UseFormReturn } from "react-hook-form";
import { TextInput } from 'src/UIKit/components/TextInput';

interface FormTextInputProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  fieldName: Path<T>;

  label?: ReactNode;
  placeholder?: string;
  helperText?: ReactNode;
  validation?: RegisterOptions;

  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  /** Keeps the label floated up, so a placeholder is readable before the field is touched. */
  shrinkLabel?: boolean;
  endIcon?: ReactNode;
  size?: TextInputProps['size'];
  variant?: "outlined" | "filled" | "standard";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export const FormTextInput = <T extends FieldValues>(props: FormTextInputProps<T>): ReactElement | null => {
  const {
    form,
    fieldName,
    label,
    placeholder,
    helperText,
    validation,
    disabled,
    multiline,
    rows,
    minRows,
    maxRows,
    shrinkLabel,
    endIcon,
    size,
    variant = "outlined",
    margin = "normal",
    fullWidth = true,
    sx,
  } = props;

  return (
    <Controller
      name={fieldName}
      control={form.control as Control<FieldValues>}
      rules={validation}
      render={({ field, fieldState }) => {
        const { value, onChange, ...rest } = field;
        const { error } = fieldState;

        return (
          <TextInput
            {...rest}
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e)}
            error={!!error}
            helperText={error?.message || helperText}
            required={!!validation?.required}
            disabled={disabled}
            multiline={multiline}
            rows={rows}
            minRows={minRows}
            maxRows={maxRows}
            shrinkLabel={shrinkLabel}
            endIcon={endIcon}
            size={size}
            variant={variant}
            fullWidth={fullWidth}
            margin={margin}
            sx={sx}
          />
        );
      }}
    />
  );
};
